import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Card,
  Typography,
  Spin,
  Empty,
  Tag,
  Space,
  Button,
  Row,
  Col,
  Statistic,
  Alert,
  Modal,
  List,
  message
} from 'antd'
import {
  CreditCardOutlined,
  BankOutlined,
  CalendarOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import { usePlaidLink } from 'react-plaid-link'
import { customerApi } from '../services/customerApi'
import { useCustomerAuth } from '../contexts/CustomerAuthContext'

const { Title, Text, Paragraph } = Typography

interface Subscription {
  id: string
  name: string
  description?: string
  amount: number
  interval: string
  intervalCount: number
  status: string
  nextBillingDate?: string
  lastBillingDate?: string
  lastPaymentAmount?: number
  lastPaymentStatus?: string
  startDate: string
  paymentMethodType: string
}

interface PaymentMethod {
  id: string
  type: string
  last4?: string
  bankName?: string
  brand?: string
  isDefault: boolean
  isVerified?: boolean
  createdAt: string
}

/** Opens Plaid Link once when mounted; parent controls whether we should open (avoids double-open on Strict Mode remount). */
function PlaidLinkOpener ({
  token,
  shouldOpen,
  onOpened,
  onSuccess,
  onExit
}: {
  token: string
  shouldOpen: boolean
  onOpened: () => void
  onSuccess: (publicToken: string) => void
  onExit: () => void
}) {
  const openedRef = useRef(false)
  const { open, ready } = usePlaidLink({
    token,
    onSuccess,
    onExit: () => onExit()
  })
  useEffect(() => {
    if (ready && shouldOpen && !openedRef.current) {
      openedRef.current = true
      onOpened()
      open()
    }
  }, [ready, shouldOpen, onOpened, open])
  return null
}

const Subscriptions: React.FC = () => {
  const { business } = useCustomerAuth()
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [loading, setLoading] = useState(true)
  const [plaidLinkToken, setPlaidLinkToken] = useState<string | null>(null)
  const [plaidLoading, setPlaidLoading] = useState(false)
  const plaidOpenedForTokenRef = useRef<string | null>(null)
  const plaidSuccessProcessedRef = useRef(false)

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    const onFocus = () => {
      // Don't refetch while Plaid Link is open; avoids re-renders that can trigger "Cannot go back" in Link
      if (plaidLinkToken) return
      fetchData()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [plaidLinkToken])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [subsResponse, methodsResponse] = await Promise.all([
        customerApi.getMySubscriptions(),
        customerApi.getMyPaymentMethods()
      ])
      setSubscriptions(subsResponse.data || [])
      setPaymentMethods(methodsResponse.data || [])
    } catch (error) {
      console.error('Error fetching subscription data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'active':
        return <Tag color="success" icon={<CheckCircleOutlined />}>Active</Tag>
      case 'paused':
        return <Tag color="warning" icon={<ClockCircleOutlined />}>Paused</Tag>
      case 'past_due':
        return <Tag color="error" icon={<ExclamationCircleOutlined />}>Past Due</Tag>
      case 'cancelled':
        return <Tag color="default">Cancelled</Tag>
      default:
        return <Tag color="processing">{status}</Tag>
    }
  }

  const getIntervalText = (interval: string, count: number) => {
    const intervalMap: Record<string, string> = {
      day: 'day',
      week: 'week',
      month: 'month',
      year: 'year'
    }
    const base = intervalMap[interval] || interval
    if (count === 1) return base
    return `${count} ${base}s`
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const handleDeletePaymentMethod = async (id: string) => {
    Modal.confirm({
      title: 'Remove Payment Method?',
      content: 'Are you sure you want to remove this payment method? This may affect your active subscriptions.',
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await customerApi.deletePaymentMethod(id)
          message.success('Payment method removed')
          fetchData()
        } catch (error) {
          message.error('Failed to remove payment method')
        }
      }
    })
  }

  const subscriptionsReturnUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}`
    : ''

  const getApiErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object' && 'response' in error) {
      const res = (error as { response?: { data?: { message?: string; error?: string } } }).response
      const msg = res?.data?.message || res?.data?.error
      if (typeof msg === 'string') return msg
    }
    return ''
  }

  const onPlaidSuccess = useCallback(
    async (publicToken: string) => {
      if (plaidSuccessProcessedRef.current) return
      plaidSuccessProcessedRef.current = true
      plaidOpenedForTokenRef.current = null
      // Delay clearing token so Plaid Link can close cleanly before we unmount (avoids "Cannot go back. Current pane does not exist")
      const clearToken = () => setPlaidLinkToken(null)
      setTimeout(clearToken, 400)
      try {
        await customerApi.exchangePlaidPublicToken(publicToken)
        message.success('Bank account linked successfully')
        fetchData()
      } catch (error) {
        const apiMsg = getApiErrorMessage(error)
        message.error(apiMsg || 'Failed to link bank account')
      }
    },
    []
  )

  const onPlaidExit = useCallback(() => {
    plaidOpenedForTokenRef.current = null
    // Delay clearing token so Plaid Link can tear down before unmount (avoids "Cannot go back. Current pane does not exist")
    setTimeout(() => setPlaidLinkToken(null), 400)
  }, [])

  const handleAddBankAccount = async () => {
    setPlaidLoading(true)
    plaidSuccessProcessedRef.current = false
    try {
      const response = await customerApi.createPlaidLinkToken()
      if (response.linkToken) {
        setPlaidLinkToken(response.linkToken)
      } else {
        message.error('Could not start bank connection')
      }
    } catch (error) {
      const apiMsg = getApiErrorMessage(error)
      message.error(apiMsg || 'Failed to start bank account setup')
    } finally {
      setPlaidLoading(false)
    }
  }

  const handleAddCard = async () => {
    try {
      const response = await customerApi.createCardSetupIntent(subscriptionsReturnUrl)
      const url = response.checkoutUrl || (response.setupIntentId ? `https://checkout.stripe.com/setup/${response.setupIntentId}` : null)
      if (url) {
        window.open(url, '_blank')
        message.info('Please complete card entry in the new window. Return here to see your saved card.')
      } else {
        message.error('Could not open card setup')
      }
    } catch (error) {
      const apiMsg = getApiErrorMessage(error)
      message.error(apiMsg || 'Failed to start card setup')
    }
  }

  // Calculate summary stats
  const activeSubscriptions = subscriptions.filter(s => s.status === 'active')
  const monthlyTotal = activeSubscriptions.reduce((sum, s) => {
    if (s.interval === 'month') return sum + s.amount
    if (s.interval === 'year') return sum + (s.amount / 12)
    if (s.interval === 'week') return sum + (s.amount * 4.33)
    return sum + s.amount
  }, 0)

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <Spin size="large" />
        <Text style={{ display: 'block', marginTop: 16 }}>Loading subscriptions...</Text>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px' }}>
      <Title level={2} style={{ marginBottom: 8 }}>
        <CreditCardOutlined style={{ marginRight: 12 }} />
        My Subscriptions
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Manage your recurring payments and payment methods with {business?.name || 'us'}
      </Paragraph>

      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8}>
          <Card size="small">
            <Statistic
              title="Active Subscriptions"
              value={activeSubscriptions.length}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card size="small">
            <Statistic
              title="Monthly Total"
              value={monthlyTotal}
              precision={2}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card size="small">
            <Statistic
              title="Payment Methods"
              value={paymentMethods.length}
              prefix={<BankOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Payment Methods Section */}
      <Card
        title={
          <Space>
            <BankOutlined />
            <span>Payment Methods</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<CreditCardOutlined />}
              onClick={handleAddCard}
              size="small"
            >
              Add card
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddBankAccount}
              loading={plaidLoading}
              size="small"
            >
              Add Bank Account
            </Button>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        {paymentMethods.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No payment methods saved"
          >
            <Space>
              <Button icon={<CreditCardOutlined />} onClick={handleAddCard}>
                Add card
              </Button>
              <Button type="primary" onClick={handleAddBankAccount} loading={plaidLoading}>
                Add Bank Account
              </Button>
            </Space>
          </Empty>
        ) : (
          <List
            dataSource={paymentMethods}
            renderItem={(method) => (
              <List.Item
                actions={[
                  <Button
                    key="remove"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeletePaymentMethod(method.id)}
                  >
                    Remove
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <div style={{
                      width: 48,
                      height: 48,
                      background: method.type === 'card' ? '#f6ffed' : '#e6f7ff',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {method.type === 'card' ? (
                        <CreditCardOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                      ) : (
                        <BankOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                      )}
                    </div>
                  }
                  title={
                    <Space>
                      <Text strong>
                        {method.type === 'card'
                          ? `${method.brand ? method.brand.charAt(0).toUpperCase() + method.brand.slice(1) : 'Card'} •••• ${method.last4 || '****'}`
                          : `${method.bankName || 'Bank Account'} •••• ${method.last4 || '****'}`}
                      </Text>
                      {method.isDefault && <Tag color="blue">Default</Tag>}
                      {(method.type === 'us_bank_account' || method.type === 'plaid_bank') && (
                        method.isVerified ? (
                          <Tag color="green">Verified</Tag>
                        ) : (
                          <Tag color="orange">Pending Verification</Tag>
                        )
                      )}
                    </Space>
                  }
                  description={
                    <Text type="secondary">
                      Added {formatDate(method.createdAt)}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>

      {/* Subscriptions Section */}
      <Card
        title={
          <Space>
            <CalendarOutlined />
            <span>Active Subscriptions</span>
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            size="small"
          >
            Refresh
          </Button>
        }
      >
        {subscriptions.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No active subscriptions"
          />
        ) : (
          <List
            dataSource={subscriptions}
            renderItem={(sub) => (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <div style={{
                      width: 56,
                      height: 56,
                      background: sub.status === 'active' ? '#f6ffed' : '#fff7e6',
                      borderRadius: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <DollarOutlined style={{
                        fontSize: 28,
                        color: sub.status === 'active' ? '#52c41a' : '#faad14'
                      }} />
                    </div>
                  }
                  title={
                    <Space direction="vertical" size={0}>
                      <Space>
                        <Text strong style={{ fontSize: 16 }}>{sub.name}</Text>
                        {getStatusTag(sub.status)}
                      </Space>
                      {sub.description && (
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {sub.description}
                        </Text>
                      )}
                    </Space>
                  }
                  description={
                    <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
                      <Col xs={24} sm={8}>
                        <Text type="secondary">Amount:</Text>{' '}
                        <Text strong>${sub.amount.toFixed(2)}</Text>
                        <Text type="secondary"> / {getIntervalText(sub.interval, sub.intervalCount)}</Text>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Text type="secondary">Next Billing:</Text>{' '}
                        <Text>{formatDate(sub.nextBillingDate)}</Text>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Text type="secondary">Started:</Text>{' '}
                        <Text>{formatDate(sub.startDate)}</Text>
                      </Col>
                    </Row>
                  }
                />
              </List.Item>
            )}
          />
        )}

        {subscriptions.some(s => s.status === 'past_due') && (
          <Alert
            message="Payment Issue"
            description="One or more of your subscriptions has a failed payment. Please update your payment method to avoid service interruption."
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
            action={
              <Button size="small" onClick={handleAddBankAccount} loading={plaidLoading}>
                Update Payment Method
              </Button>
            }
          />
        )}
      </Card>

      {plaidLinkToken && (
        <PlaidLinkOpener
          token={plaidLinkToken}
          shouldOpen={plaidOpenedForTokenRef.current !== plaidLinkToken}
          onOpened={() => { plaidOpenedForTokenRef.current = plaidLinkToken }}
          onSuccess={onPlaidSuccess}
          onExit={onPlaidExit}
        />
      )}
    </div>
  )
}

export default Subscriptions

